import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImperativePanelHandle, getPanelElement } from 'react-resizable-panels'

import { debounce } from 'lodash-es'

type PanelDimensions = {
    width: number
    height: number
}

export const usePanelDimensions = (isReadyToDisplayContainer: boolean, debounceMs = 300) => {
    const panelRef = useRef<ImperativePanelHandle>(null)
    const [dimensions, setDimensions] = useState<PanelDimensions>({
        width: 0,
        height: 0,
    })

    const resizeRef = useRef<() => void>(() => {})
    const observedRef = useRef<{ observer: ResizeObserver; element: HTMLElement } | null>(null)

    // The panel library reports a resize only when the layout's percentages change, so with the AI panel hidden a
    // window resize keeps the transcript at 100% and reports nothing. Follow the element itself. The panels remount
    // when a transcript error clears, and the new panel's first resize report switches the observer to it.
    const followPanelElement = useCallback((element: HTMLElement) => {
        if (observedRef.current?.element === element) return

        observedRef.current?.observer.disconnect()
        const observer = new ResizeObserver(() => resizeRef.current())
        observer.observe(element)
        observedRef.current = { observer, element }
    }, [])

    useEffect(() => () => observedRef.current?.observer.disconnect(), [])

    const updatePanelDimensions = useCallback(() => {
        if (!panelRef.current) return

        const panelId = panelRef.current.getId()
        const panelElement = getPanelElement(panelId)
        if (!panelElement) return

        followPanelElement(panelElement)
        const { clientWidth, clientHeight } = panelElement
        // The observer also reports changes that leave the size as it was; a new object would re-render the app.
        setDimensions((previous) =>
            previous.width === clientWidth && previous.height === clientHeight ? previous : { width: clientWidth, height: clientHeight },
        )
    }, [followPanelElement])

    const handlePanelResize = useMemo(() => debounce(updatePanelDimensions, debounceMs), [updatePanelDimensions, debounceMs])

    useEffect(() => {
        resizeRef.current = handlePanelResize

        return () => handlePanelResize.cancel()
    }, [handlePanelResize])

    // Initialize panel dimensions
    useEffect(() => {
        if (isReadyToDisplayContainer) {
            updatePanelDimensions()
        }
    }, [isReadyToDisplayContainer, updatePanelDimensions])

    return {
        panelRef,
        dimensions,
        handlePanelResize,
        updatePanelDimensions,
    }
}
